import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule, FormBuilder, Validators, ValidationErrors } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AuthService } from '../../../services/account/auth.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { AccountCentreService } from '../../../account-centre/account-centre.service';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';
import { isFieldInvalid } from '../../shared/validators/form-validation.utils';

// ── Custom DOB Validator ──────────────────────────────────────────────────────
export function dobValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  const dob = value instanceof Date ? value : new Date(value);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (dob >= today) return { dobFuture: true };
  const ageDiff = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();
  let age = ageDiff;
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;
  if (age < 10 || age > 100) return { dobAgeRange: true };
  return null;
}

@Component({
  selector: 'app-da-personal-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    AutoCompleteModule,
    ButtonModule,
    ToastModule,
    FieldErrorComponent
  ],
  providers: [MessageService],
  templateUrl: './personal-details.component.html',
  styleUrl: './personal-details.component.scss'
})
export class DaPersonalDetailsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Output() loaded = new EventEmitter<void>();

  isDataExists: boolean = false;
  isSaving: boolean = false;

  genderOptions = [
    { label: 'Male', value: 1 },
    { label: 'Female', value: 2 },
    { label: 'Other', value: 3 }
  ];

  locationSuggestions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private utilityService: UtilityService,
    private accountCentreService: AccountCentreService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    if (!this.parentForm.get('personalDetails')) {
      const group = this.fb.group({
        firstName: ['', FieldValidators.personName],
        lastName: ['', FieldValidators.personName],
        dob: [null, [Validators.required, dobValidator]],
        gender: [null, FieldValidators.requiredOnly],
        currentLocation: ['', FieldValidators.requiredOnly]
      });
      this.parentForm.addControl('personalDetails', group);
    }

    this.loadUserData();
  }

  get personalDetailsForm(): FormGroup {
    return this.parentForm.get('personalDetails') as FormGroup;
  }

  readonly isFieldInvalid = isFieldInvalid;

  searchLocation(event: any): void {
    const q = String(event?.query || '').trim();
    if (!q || q.length < 3) {
      this.locationSuggestions = [];
      return;
    }

    this.utilityService.getOLAPlaces(q).subscribe({
      next: (response: any) => {
        this.locationSuggestions = response?.predictions || [];
      },
      error: (err) => {
        console.error('Failed to fetch OLA places:', err);
        this.locationSuggestions = [];
      }
    });
  }

  onLocationSelect(event: any): void {
    const prediction = event.value;
    const description = prediction?.description || prediction || '';
    this.personalDetailsForm.get('currentLocation')?.setValue(description);
  }

  loadUserData() {
    try {
      const raw = localStorage.getItem('account');
      if (raw) {
        const account = JSON.parse(raw);
        const userId = account?.userId;
        if (userId) {
          this.authService.getUserByid(userId).subscribe({
            next: (user: any) => {
              if (user) {
                this.isDataExists = true;
                let genderVal: number | null = null;
                if (user.gender !== null && user.gender !== undefined) {
                  const g = String(user.gender).toLowerCase();
                  if (g === '1' || g === 'male') genderVal = 1;
                  else if (g === '2' || g === 'female') genderVal = 2;
                  else if (g === '3' || g === 'other') genderVal = 3;
                }

                // Parse DOB carefully: strip time so local date is used
                let dobDate: Date | null = null;
                if (user.bornOn) {
                  const rawDob = String(user.bornOn);
                  // Handle ISO strings like 2006-04-06T00:00:00 — take only date part
                  const datePart = rawDob.split('T')[0];
                  const parts = datePart.split('-');
                  if (parts.length === 3) {
                    dobDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                  } else {
                    dobDate = new Date(rawDob);
                  }
                  if (isNaN(dobDate.getTime())) dobDate = null;
                }

                this.personalDetailsForm.patchValue({
                  firstName: user.firstName || '',
                  lastName: user.lastName || '',
                  dob: dobDate,
                  gender: genderVal,
                  currentLocation: user.location || ''
                });
              }
              this.loaded.emit();
            },
            error: (err) => {
              console.warn('Failed to load user details for autofill:', err);
              this.loaded.emit();
            }
          });
        }
      }
    } catch (e) {
      console.error('Error loading user profile:', e);
    }
  }

  savePersonalInfo() {
    this.personalDetailsForm.markAllAsTouched();
    if (this.personalDetailsForm.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError();
      return;
    }

    if (!this.personalDetailsForm.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return;
    }

    const val = this.personalDetailsForm.value;

    const dobObj = val.dob;
    let dateOfBirth = null;
    if (dobObj instanceof Date) {
      dateOfBirth = `${dobObj.getFullYear()}-${String(dobObj.getMonth() + 1).padStart(2, '0')}-${String(dobObj.getDate()).padStart(2, '0')}`;
    }

    const personBody: any = {
      firstName: val.firstName,
      lastName: val.lastName,
      gender: val.gender,
      dateOfBirth: dateOfBirth
    };

    const studentBody: any = {
      location: typeof val.currentLocation === 'object' ? val.currentLocation?.description || '' : val.currentLocation || ''
    };

    this.isSaving = true;
    forkJoin({
      person: this.accountCentreService.updateMyPerson(personBody),
      student: this.accountCentreService.updateMyStudentProfile(studentBody)
    }).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDataExists = true;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Personal details saved successfully.' });
        this.personalDetailsForm.markAsPristine();
      },
      error: (err) => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to save personal details.' });
      }
    });
  }

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
